import { Link } from "react-router-dom";
import type { Integration } from "../../../types/integration";
import { Logo } from "../Logo";
import styles from "./ActiveIntegrations.module.css";

type ActiveIntegrationsProps = {
  readonly integrations: ReadonlyArray<Integration>;
};

// A row's display state, derived from BOTH the legacy lifecycle (`status`)
// and the live connection (`connectionStatus`). error/revoked is the most
// urgent so it wins; then anything still being set up; otherwise active.
type RowState = "active" | "setup" | "error";

export const rowStateOf = (i: Integration): RowState => {
  if (i.connectionStatus === "error" || i.connectionStatus === "revoked") {
    return "error";
  }
  if (i.connectionStatus === "pending") {
    return "setup";
  }
  // A legacy 'provisioning' is stale once a live connection exists.
  const connected = i.connectionStatus !== null && i.connectionStatus !== "disconnected";
  if (i.status === "provisioning" && !connected) {
    return "setup";
  }
  return "active";
};

const errorLabel = (i: Integration): string =>
  i.connectionStatus === "revoked" ? "Access revoked" : "Connection failed";

const PlugIcon = (): JSX.Element => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 2v6M15 2v6M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8zM12 17v5" />
  </svg>
);

export const ActiveIntegrations = ({
  integrations,
}: ActiveIntegrationsProps): JSX.Element => {
  if (integrations.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.head}>
          <span className={styles.eyebrow}>// ACTIVE INTEGRATIONS</span>
        </div>
        <div className={styles.empty}>
          <span className={styles.emptyIcon} aria-hidden>
            <PlugIcon />
          </span>
          <div className={styles.emptyBody}>
            <p className={styles.emptyTitle}>Nothing connected yet.</p>
            <p className={styles.emptyHint}>
              Pick an integration below and your agent gets to work in minutes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Derive each row's state once, then reuse for both the summary counts and
  // the rendered rows (rather than calling rowStateOf twice per integration).
  const rows = integrations.map((integration) => ({ integration, state: rowStateOf(integration) }));
  const activeCount = rows.filter((r) => r.state === "active").length;
  const setupCount = rows.filter((r) => r.state === "setup").length;
  const errorCount = rows.filter((r) => r.state === "error").length;
  const summaryParts: string[] = [];
  if (activeCount > 0) summaryParts.push(`${activeCount} connected`);
  if (setupCount > 0) summaryParts.push(`${setupCount} in setup`);
  if (errorCount > 0) summaryParts.push(`${errorCount} needs attention`);
  const summary = summaryParts.join(" · ");

  return (
    <div className={styles.wrap}>
      <div className={styles.head}>
        <span className={styles.eyebrow}>// ACTIVE INTEGRATIONS</span>
        <span className={styles.count}>{summary}</span>
      </div>
      <ul className={styles.list}>
        {rows.map(({ integration, state }) => {
          // IP-255: surface multiple connected accounts. The count drives the
          // pill ("2 accounts") and, when >1, the account names replace the
          // category subtitle so you can see which accounts at a glance. An
          // unnamed primary shows as "Primary".
          const activeAccounts = (integration.connections ?? []).filter(
            (c) => c.status === "active",
          );
          const multi = activeAccounts.length > 1;
          const accountNames = activeAccounts.map((c) => c.alias ?? "Primary").join(" · ");
          return (
            <li key={integration.slug} className={styles.row}>
              <Link
                to={`/dashboard/integrations/${integration.slug}`}
                className={styles.rowLink}
                aria-label={`Open ${integration.title}`}
              >
                <span className={styles.logoWrap}>
                  <Logo
                    slug={integration.slug}
                    size={28}
                    brandHex={integration.iconBrandHex ?? null}
                    logoUrl={
                      integration.ourLogoUrl ?? integration.logoUrl ?? null
                    }
                  />
                </span>
                <div className={styles.body}>
                  <span className={styles.name}>{integration.title}</span>
                  <span
                    className={styles.category}
                    title={multi ? accountNames : undefined}
                  >
                    {multi ? accountNames : integration.category}
                  </span>
                </div>
                {state === "error" ? (
                  <span className={styles.statusError}>
                    <span className={styles.errorDot} aria-hidden />
                    <span>{errorLabel(integration)}</span>
                  </span>
                ) : state === "setup" ? (
                  <span className={styles.statusSetup}>
                    <span className={styles.setupDot} aria-hidden />
                    <span>In setup</span>
                  </span>
                ) : (
                  <span className={styles.statusActive}>
                    <span className={styles.activeDot} aria-hidden />
                    <span>{multi ? `Active (${activeAccounts.length})` : "Active"}</span>
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
